import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/AcquireFooter';
import { SEOHead } from '../components/SEO/SEOHead';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12781423/ufbg7ds/';
      // Create form data
      const webhookData = new URLSearchParams();
      webhookData.append('name', formData.name);
      webhookData.append('email', formData.email);
      webhookData.append('phone', formData.phone || 'Not provided');
      webhookData.append('company', formData.company || 'Not provided');
      webhookData.append('message', formData.message);
      webhookData.append('timestamp', new Date().toISOString());
      webhookData.append('source', 'Contact Page');
      const response = await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: webhookData.toString()
      });
      if (!response.ok) {
        throw new Error('Webhook submission failed');
      }
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      // Still show success for better UX
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <>
      <SEOHead title="Contact Us - AcquireFlow" description="Get in touch with the AcquireFlow team. We're here to help you automate your real estate investment workflow." canonical="https://acquireflow.com/contact" data-id="element-2504" />

      <div className="flex flex-col min-h-screen bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707]" data-id="element-2505">
        <Navbar data-id="element-2506" />

        <main className="flex-grow" data-id="element-2507">
          {/* Hero Section */}
          <section className="relative w-full bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707] py-20 px-6" data-id="element-2508">
            <div className="max-w-4xl mx-auto text-center" data-id="element-2509">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm mb-6" data-id="element-2510">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-2511" />
                <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-2512">
                  Get In Touch
                </p>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight mb-6" data-id="element-2513">
                Let's Talk About Your{' '}
                <span className="text-[#3ab795] relative inline-block" data-id="element-2514">
                  Deal Flow
                  <span className="absolute -bottom-2 left-0 right-0 h-1 bg-[#3ab795]/30 rounded-full" data-id="element-2515"></span>
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-[#b8bcc8] leading-relaxed font-light max-w-3xl mx-auto" data-id="element-2516">
                Have questions about AcquireFlow? Our team is here to help you
                automate your real estate investment workflow.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="relative w-full bg-gradient-to-b from-[#070707] via-[#0a0a0a] to-[#070707] py-20 px-6" data-id="element-2517">
            <div className="max-w-6xl mx-auto" data-id="element-2518">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2519">
                {/* Contact Form */}
                <div className="bg-gradient-to-br from-[#1a1a1a]/80 to-[#0f0f0f]/80 border border-white/10 rounded-2xl p-8 backdrop-blur-md" data-id="element-2520">
                  <h2 className="text-2xl font-bold text-white mb-6" data-id="element-2521">
                    Send Us a Message
                  </h2>

                  {!isSubmitted ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2522">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2523">
                        <div data-id="element-2524">
                          <label htmlFor="name" className="block text-sm font-medium text-white mb-2" data-id="element-2525">
                            Full Name *
                          </label>
                          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" placeholder="John Doe" required data-id="element-2526" />
                        </div>

                        <div data-id="element-2527">
                          <label htmlFor="email" className="block text-sm font-medium text-white mb-2" data-id="element-2528">
                            Email Address *
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" placeholder="john@example.com" required data-id="element-2529" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2530">
                        <div data-id="element-2531">
                          <label htmlFor="phone" className="block text-sm font-medium text-white mb-2" data-id="element-2532">
                            Phone Number
                          </label>
                          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" placeholder="(555) 123-4567" data-id="element-2533" />
                        </div>

                        <div data-id="element-2534">
                          <label htmlFor="company" className="block text-sm font-medium text-white mb-2" data-id="element-2535">
                            Company Name
                          </label>
                          <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} disabled={isSubmitting} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Your Company" data-id="element-2536" />
                        </div>
                      </div>

                      <div data-id="element-2537">
                        <label htmlFor="message" className="block text-sm font-medium text-white mb-2" data-id="element-2538">
                          Message *
                        </label>
                        <textarea id="message" name="message" value={formData.message} onChange={handleChange} disabled={isSubmitting} rows={6} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Tell us about your needs..." required data-id="element-2539" />
                      </div>

                      <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-3 bg-[#3ab795] hover:bg-[#2da07d] text-white font-medium py-4 rounded-xl transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#0a0a0a] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-2540">
                        {isSubmitting ? <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2541">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2542"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2543"></path>
                            </svg>
                            <span data-id="element-2544">Sending...</span>
                          </> : <>
                            <span data-id="element-2545">Send Message</span>
                            <Send className="w-5 h-5" data-id="element-2546" />
                          </>}
                      </button>
                    </form> : <div className="text-center py-12 animate-fade-in" data-id="element-2547">
                      <div className="w-16 h-16 bg-[#3ab795]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2548">
                        <CheckCircle className="w-8 h-8 text-[#3ab795]" strokeWidth={2} data-id="element-2549" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2550">
                        Message Sent!
                      </h3>
                      <p className="text-[#b8bcc8] mb-6" data-id="element-2551">
                        Thanks for reaching out. We'll get back to you within 24
                        hours at {formData.email || 'your email'}.
                      </p>
                      <button onClick={() => setIsSubmitted(false)} className="inline-flex items-center gap-2 text-[#3ab795] hover:text-[#2da07d] font-medium transition-colors" data-id="element-2552">
                        Send Another Message
                      </button>
                    </div>}
                </div>

                {/* Contact Information */}
                <div className="space-y-8" data-id="element-2553">
                  <div data-id="element-2554">
                    <h2 className="text-2xl font-bold text-white mb-6" data-id="element-2555">
                      Contact Information
                    </h2>
                    <p className="text-[#b8bcc8] mb-8" data-id="element-2556">
                      Reach out to us through any of these channels. We
                      typically respond within 24 hours.
                    </p>
                  </div>

                  <div className="space-y-6" data-id="element-2557">
                    <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-[#1a1a1a]/60 to-[#0f0f0f]/60 border border-white/10 rounded-xl" data-id="element-2558">
                      <div className="w-12 h-12 bg-[#3ab795]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2559">
                        <Mail className="w-6 h-6 text-[#3ab795]" data-id="element-2560" />
                      </div>
                      <div data-id="element-2561">
                        <h3 className="text-white font-semibold mb-1" data-id="element-2562">Email</h3>
                        <a href="mailto:support@acquireflow.ai" className="text-[#b8bcc8] hover:text-[#3ab795] transition-colors" data-id="element-2563">
                          support@acquireflow.ai
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-[#1a1a1a]/60 to-[#0f0f0f]/60 border border-white/10 rounded-xl" data-id="element-2564">
                      <div className="w-12 h-12 bg-[#3ab795]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2565">
                        <Phone className="w-6 h-6 text-[#3ab795]" data-id="element-2566" />
                      </div>
                      <div data-id="element-2567">
                        <h3 className="text-white font-semibold mb-1" data-id="element-2568">Phone</h3>
                        <a href="tel:+18052090120" className="text-[#b8bcc8] hover:text-[#3ab795] transition-colors" data-id="element-2569">
                          +1 805-209-0120
                        </a>
                        <p className="text-sm text-[#b8bcc8]/70 mt-1" data-id="element-2570">
                          Mon-Fri, 9am-6pm EST
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-[#1a1a1a]/60 to-[#0f0f0f]/60 border border-white/10 rounded-xl" data-id="element-2571">
                      <div className="w-12 h-12 bg-[#3ab795]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2572">
                        <MapPin className="w-6 h-6 text-[#3ab795]" data-id="element-2573" />
                      </div>
                      <div data-id="element-2574">
                        <h3 className="text-white font-semibold mb-1" data-id="element-2575">
                          Office
                        </h3>
                        <p className="text-[#b8bcc8]" data-id="element-2576">
                          ACQUIRE FLOW LLC
                          <br data-id="element-2577" />
                          254 Chapman Rd, Ste 208 #25827
                          <br data-id="element-2578" />
                          Newark, Delaware 19702 US
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Box */}
                  <div className="bg-gradient-to-br from-[#3ab795]/10 to-[#3ab795]/5 border border-[#3ab795]/20 rounded-xl p-6" data-id="element-2579">
                    <h3 className="text-white font-semibold mb-2" data-id="element-2580">
                      Ready to Get Started?
                    </h3>
                    <p className="text-[#b8bcc8] mb-4" data-id="element-2581">
                      Start your 10-day free trial today. No credit card
                      required.
                    </p>
                    <a href="#pricing" className="inline-flex items-center gap-2 text-[#3ab795] hover:text-[#2da07d] font-medium transition-colors" data-id="element-2582">
                      View Pricing Plans
                      <Send className="w-4 h-4" data-id="element-2583" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer data-id="element-2584" />
      </div>
    </>;
}